import { toast } from "@jammable/ui-core";
import React, { useState } from "react";
import useAuthModal from "../../hooks/useAuthModal";
import { getIdToken } from "../../utils/firebaseAuth";
import axios from "axios";
import { formatPriceWithLocale, getPriceId } from "../../utils/pricing";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaTrigger,
} from "@jammable/ui-components/Dialog";
import { Upgrade } from "@jammable/ui-payments-feature";
import { useRouter } from "next/router";
import { Check } from "lucide-react";

const PLAN_LABEL = { basic: "Basic", creator: "Creator", power: "Power" };

/**
 * Start a checkout: creates the incomplete subscription and returns the
 * client secret for the Stripe form. PlanCTA's button and the paywall's
 * one-tap reopen (UpgradeModal autoCheckout) both come through here.
 */
export async function startCheckout({
  plan,
  currency,
  billingInterval = "monthly",
  source = "pricing-page",
  offer = null,
}) {
  const interval = billingInterval === "annual" ? "year" : "month";
  let checkoutCurrency = currency;
  let priceId = getPriceId(plan, currency, interval);
  if (plan === "power" && !priceId) {
    checkoutCurrency = "USD";
    priceId = getPriceId(plan, "USD", interval);
  }
  const token = await getIdToken();
  const res = await axios.post(
    `/api/payment/createSubscriptionIntent`,
    {
      priceId,
      refId: window?.tolt_referral,
      currency: checkoutCurrency,
      from: source,
      ...(offer ? { offer, offerPlan: plan } : {}),
    },
    { headers: { Authorization: token } },
  );
  return res.data.clientSecret;
}

export const checkoutError = (e) =>
  e?.response?.status === 409 && e.response.data?.error
    ? e.response.data.error
    : "Something went wrong. Please contact support!";

/**
 * The plan button on its own: active / manage / upgrade / checkout, chosen
 * from the visitor's current subscription. Shared by the pricing cards and
 * the upgrade modal's single recommended plan so both start exactly the same
 * checkout (createSubscriptionIntent with the same priceId, currency and
 * `from`). Change checkout here, never in a copy.
 */
export const PlanCTA = ({
  plan,
  status,
  currency,
  setClientSecret,
  onSelect,
  billingInterval = "monthly",
  source = "pricing-page",
  onUpgradeClick = null,
  upgradeLoading = false,
  label,
  className = "",
  // "welcome-back": the server swaps in the returning-subscriber price for
  // this plan (lib/welcomeBack).
  offer = null,
}) => {
  const isBasic = plan === "basic";
  const isCreator = plan === "creator";
  const isPower = plan === "power";
  const { checkAuthAndExecute } = useAuthModal();
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const redirectToManageSubscription = async () => {
    const token = await getIdToken();
    axios
      .post(`/api/payment/manageSubscription`, undefined, {
        headers: { Authorization: token },
      })
      .then((res) => (window.location.href = res.data.url))
      .catch(() =>
        toast.error("Something went wrong, please contact support!"),
      );
  };

  const isActive =
    (isBasic && status === "basic") ||
    (isCreator && status === "creator") ||
    (isPower && status === "power");

  const canUpgrade =
    (status === "basic" && (isCreator || isPower)) ||
    (status === "creator" && isPower);

  const hasHigherPlan =
    (status === "creator" && isBasic) ||
    (status === "power" && (isBasic || isCreator));

  const upgradeTarget = isCreator
    ? "Jammable Creator"
    : isPower
      ? "Jammable Power User"
      : null;

  const base = `tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-px-4 tw-py-4 tw-text-base tw-font-extrabold tw-cursor-pointer tw-transition active:tw-scale-[0.98] disabled:tw-cursor-default ${className}`;

  if (isActive) {
    return (
      <button disabled className={`${base} !tw-bg-jam-lav !tw-text-jam-muted`}>
        <Check size={16} strokeWidth={3} />
        Your plan
      </button>
    );
  }
  if (hasHigherPlan) {
    return (
      <button onClick={() => redirectToManageSubscription()} className={base}>
        Manage plan
      </button>
    );
  }
  if (canUpgrade && upgradeTarget) {
    const upgradeLabel = `Upgrade to ${PLAN_LABEL[plan]}`;
    if (onUpgradeClick) {
      return (
        <button
          className={base}
          onClick={() => onUpgradeClick(upgradeTarget)}
          disabled={upgradeLoading}
        >
          {upgradeLoading ? (
            <Spinner size={4} className="tw-border-3 tw-border-current" />
          ) : (
            upgradeLabel
          )}
        </button>
      );
    }
    return (
      <Credenza>
        <CredenzaTrigger asChild>
          <button className={base}>{upgradeLabel}</button>
        </CredenzaTrigger>
        <CredenzaContent>
          <CredenzaBody>
            <Upgrade
              type={upgradeTarget}
              fallback={() => redirectToManageSubscription()}
              callback={() => {
                toast.success("Your subscription has been updated!");
                setTimeout(() => router.push("/settings"), 1500);
              }}
            />
          </CredenzaBody>
        </CredenzaContent>
      </Credenza>
    );
  }

  return (
    <button
      onClick={async () => {
        if (!checkAuthAndExecute()) return false;
        if (onSelect) onSelect();
        setLoading(true);
        startCheckout({ plan, currency, billingInterval, source, offer })
          .then((clientSecret) => {
            setClientSecret(clientSecret);
            setTimeout(() => setLoading(false), 0);
          })
          .catch((e) => {
            toast.error(checkoutError(e));
            setLoading(false);
          });
      }}
      className={base}
    >
      {loading ? (
        <Spinner size={4} className="tw-border-3 tw-border-current" />
      ) : (
        label || `Get ${PLAN_LABEL[plan]}`
      )}
    </button>
  );
};

/**
 * One plan card in design language F. `price` is what the first charge is;
 * `originalPrice`, when set, is the standing monthly rate shown struck
 * through (first-month promo). For annual billing pass the yearly price and
 * no originalPrice.
 */
const PricingCard = ({
  plan,
  status,
  currency,
  price,
  originalPrice,
  benefits,
  everythingIn = null,
  setClientSecret,
  onSelect,
  featured = false,
  badge = null,
  subtitle = "",
  billingInterval = "monthly",
  source = "pricing-page",
  onUpgradeClick = null,
  upgradeLoading = false,
}) => {
  const isAnnual = billingInterval === "annual";
  const fmt = (n) => formatPriceWithLocale(n, currency);

  const priceNote = isAnnual
    ? `a year, about ${fmt(Math.round((price / 12) * 100) / 100)} a month`
    : originalPrice != null
      ? `first month, then ${fmt(originalPrice)} a month`
      : "a month";

  const ctaLabel =
    !isAnnual && originalPrice != null
      ? `Start for ${fmt(price)}`
      : `Get ${PLAN_LABEL[plan]}`;

  const ctaClass = featured
    ? "tw-bg-jam-lime tw-text-jam-ink hover:tw-brightness-95"
    : plan === "basic"
      ? "tw-bg-jam-ink tw-text-white hover:tw-bg-jam-deep"
      : "tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line";

  return (
    <div
      className={`tw-relative tw-flex tw-h-full tw-flex-col tw-gap-4 ${
        featured
          ? "tw-rounded-[32px] tw-bg-jam-purple tw-p-[22px] tw-text-white md:tw-rounded-[40px] md:tw-p-[30px]"
          : "tw-rounded-[32px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-[22px] tw-text-jam-ink md:tw-rounded-[36px] md:tw-p-7"
      }`}
    >
      {badge && (
        <span className="tw-absolute tw--top-3.5 tw-left-[30px] tw-rounded-full tw-bg-jam-lime tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-jam-ink">
          {badge}
        </span>
      )}
      <div>
        <div className="jam-display tw-text-[24px] tw-font-extrabold md:tw-text-[28px]">
          {PLAN_LABEL[plan]}
        </div>
        <div
          className={`tw-text-sm tw-font-semibold ${
            featured ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
          }`}
        >
          {subtitle}
        </div>
      </div>

      <div>
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
          <span
            className={`jam-display tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] ${
              featured
                ? "tw-text-[44px] md:tw-text-[56px]"
                : "tw-text-[40px] md:tw-text-[52px]"
            }`}
          >
            {fmt(price)}
          </span>
          {!isAnnual && originalPrice != null && (
            <span
              className={`tw-text-base tw-font-bold tw-line-through ${
                featured ? "tw-text-jam-soft" : "tw-text-jam-muted"
              }`}
            >
              {fmt(originalPrice)}
            </span>
          )}
        </div>
        <div
          className={`tw-mt-1 tw-text-[13px] tw-font-bold ${
            featured ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
          }`}
        >
          {priceNote}
        </div>
      </div>

      <PlanCTA
        plan={plan}
        status={status}
        currency={currency}
        setClientSecret={setClientSecret}
        onSelect={onSelect}
        billingInterval={billingInterval}
        source={source}
        onUpgradeClick={onUpgradeClick}
        upgradeLoading={upgradeLoading}
        label={ctaLabel}
        className={ctaClass}
      />

      <div className="tw-flex tw-flex-col tw-gap-2.5 tw-text-sm tw-font-bold">
        {everythingIn && (
          <span
            className={featured ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"}
          >
            Everything in {everythingIn}, plus
          </span>
        )}
        {benefits.map((benefit) => (
          <span key={benefit} className="tw-flex tw-items-start tw-gap-2">
            <Check
              size={16}
              strokeWidth={3}
              className={`tw-mt-0.5 tw-flex-shrink-0 ${
                featured ? "tw-text-jam-lime" : "tw-text-jam-purple"
              }`}
            />
            {benefit}
          </span>
        ))}
      </div>
    </div>
  );
};

export default PricingCard;
